import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useToast } from "@/components/Toaster";
import { api, errorDetail, errorMessage } from "@/lib/api";
import { isPackNotInstalled, PackNotInstalled } from "@/components/PackNotInstalled";

export const Route = createFileRoute("/_auth/concrete-plant/load-to-cash")({
  component: ConcretePlantLoadToCashPage,
});

type ProjectionException = {
  code: string;
  detail: string;
  next_action: string;
};

type PricedLine = {
  line_no: number;
  component_key: string;
  description: string;
  quantity: string | number;
  unit_price: string | number;
  amount: string | number;
  tax_amount: string | number;
};

type Ticket = {
  ticket_source_id: string;
  ticket_source_version: number;
  status: "clean" | "exception" | "not_accepted" | "already_invoiced";
  customer_job_profile: Record<string, string | boolean | null>;
  chain: Record<string, string>;
  timeline: Record<string, string>;
  billing_readiness: { ready: boolean; blockers: string[]; next_actions: string[] };
  exceptions: ProjectionException[];
  pricing: null | {
    currency: string;
    subtotal: string | number;
    tax_total: string | number;
    total: string | number;
    lines: PricedLine[];
  };
  invoice_proposal: null | { invoice_no: string; proposal_sha256: string };
  delivered_not_invoiced: boolean;
  lineage: {
    invoice_id?: string | null;
    invoice_no?: string | null;
    invoice_status?: string | null;
    receipt_ids?: string[];
    journal_id?: string | null;
    paid_amount?: string;
  };
};

type LoadToCashResponse = {
  invoice_date: string;
  period_id: string;
  period_start: string;
  period_end: string;
  projection: {
    projection_version_id: string;
    composition_sha256: string;
    projection_sha256: string;
    control_totals: Record<string, string | number>;
    tickets: Ticket[];
  };
};

type MaterializationResponse = {
  materialization: {
    invoice_id: string;
    invoice_no: string;
    status: string;
    created: boolean;
  };
};

const queryKey = ["concrete-plant", "load-to-cash"] as const;

function money(value: string | number | null | undefined, currency = "USD") {
  return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(Number(value ?? 0));
}

function quantity(value: string | number | null | undefined) {
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 4 }).format(Number(value ?? 0));
}

function statusTone(status: Ticket["status"]) {
  if (status === "clean") return "border-emerald-200 bg-emerald-50 text-emerald-800";
  if (status === "already_invoiced") return "border-blue-200 bg-blue-50 text-blue-800";
  if (status === "exception") return "border-rose-200 bg-rose-50 text-rose-800";
  return "border-slate-200 bg-slate-50 text-slate-700";
}

function ConcretePlantLoadToCashPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const projection = useQuery({
    queryKey,
    queryFn: () => api<LoadToCashResponse>("/api/implementation/concrete-plant/load-to-cash"),
  });
  const createInvoice = useMutation({
    mutationFn: (ticketSourceId: string) =>
      api<MaterializationResponse>(
        `/api/implementation/concrete-plant/load-to-cash/tickets/${encodeURIComponent(ticketSourceId)}/invoice-proposal`,
        { method: "POST" },
      ),
    onSuccess: async (result) => {
      await qc.invalidateQueries({ queryKey });
      toast.success(
        result.materialization.created
          ? `Draft invoice ${result.materialization.invoice_no} created`
          : `Draft invoice ${result.materialization.invoice_no} already exists`,
      );
      void navigate({
        to: "/invoices/ar/$invoiceId",
        params: { invoiceId: result.materialization.invoice_id },
      });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const data = projection.data;
  const error = projection.error;
  const structuredError = errorDetail(error);

  return (
    <div className="min-h-full bg-slate-50 dark:bg-gray-950">
      <header className="border-b border-slate-200 bg-white px-6 py-5 dark:border-gray-800 dark:bg-gray-900">
        <div className="mx-auto flex max-w-7xl items-start justify-between gap-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-600">Concrete Plant</p>
            <h1 className="mt-1 text-2xl font-semibold text-slate-950 dark:text-white">Load to cash</h1>
            <p className="mt-1 max-w-3xl text-sm text-slate-600 dark:text-slate-300">
              Turn accepted delivery tickets into canonical AR invoices, then follow cash and journal lineage.
              The view is rebuilt from shared source envelopes and contracts; it does not store plant transactions.
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            <Link
              to="/ar"
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:border-blue-400 hover:text-blue-700 dark:border-gray-700 dark:bg-gray-900 dark:text-slate-200"
            >
              All invoices
            </Link>
            <button
              type="button"
              onClick={() => void projection.refetch()}
              disabled={projection.isFetching}
              className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-700 disabled:opacity-50 dark:bg-blue-600 dark:hover:bg-blue-500"
            >
              {projection.isFetching ? "Refreshing…" : "Refresh projection"}
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl space-y-5 px-6 py-6">
        {projection.isLoading && (
          <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-600 dark:border-gray-800 dark:bg-gray-900 dark:text-slate-300">
            Rebuilding current tickets from shared primitives…
          </div>
        )}

        {error && isPackNotInstalled(error) && <PackNotInstalled label="Concrete Plant" error={error} />}
        {error && !isPackNotInstalled(error) && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-5 text-sm text-rose-900" role="alert">
            <div className="font-semibold">Load-to-cash could not be rebuilt.</div>
            <p className="mt-1">{structuredError?.detail ?? errorMessage(error)}</p>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() => void projection.refetch()}
                className="rounded-md bg-rose-700 px-3 py-2 font-semibold text-white hover:bg-rose-800"
              >
                Retry
              </button>
              {structuredError?.action && (
                <Link
                  to={structuredError.action.href}
                  className="rounded-md border border-rose-300 bg-white px-3 py-2 font-semibold text-rose-800 hover:bg-rose-100"
                >
                  {structuredError.action.label}
                </Link>
              )}
            </div>
          </div>
        )}

        {data && (
          <>
            <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5" aria-label="Load-to-cash control totals">
              {[
                ["Accepted tickets", data.projection.control_totals.tickets_accepted],
                ["Needs review", data.projection.control_totals.tickets_exception],
                ["Unbilled", data.projection.control_totals.tickets_unbilled],
                ["Invoiced", data.projection.control_totals.tickets_invoiced],
                ["Paid", money(data.projection.control_totals.paid)],
              ].map(([label, value]) => (
                <div key={label} className="rounded-xl border border-slate-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
                  <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
                  <div className="mt-1 text-2xl font-semibold text-slate-950 dark:text-white">{value}</div>
                </div>
              ))}
            </section>

            <section className="rounded-xl border border-slate-200 bg-white dark:border-gray-800 dark:bg-gray-900">
              <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-gray-800">
                <div>
                  <h2 className="font-semibold text-slate-950 dark:text-white">Delivery tickets</h2>
                  <p className="mt-0.5 text-xs text-slate-500">
                    Invoice date {data.invoice_date} · open period {data.period_start}–{data.period_end}
                  </p>
                </div>
                <span className="text-xs text-slate-500">{data.projection.tickets.length} current</span>
              </div>

              {data.projection.tickets.length === 0 ? (
                <div className="p-8 text-center">
                  <h3 className="font-semibold text-slate-900 dark:text-white">No current delivery tickets</h3>
                  <p className="mx-auto mt-1 max-w-xl text-sm text-slate-600 dark:text-slate-300">
                    Import or connect dispatch, batch, and ticket source records. They will appear here after normalization into shared envelopes.
                  </p>
                  <Link to="/imports" className="mt-4 inline-flex rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700">
                    Open imports
                  </Link>
                </div>
              ) : (
                <div className="divide-y divide-slate-200 dark:divide-gray-800">
                  {data.projection.tickets.map((ticket) => {
                    const invoiceId = ticket.lineage.invoice_id;
                    const openInvoice = () => {
                      if (!invoiceId) return;
                      void navigate({ to: "/invoices/ar/$invoiceId", params: { invoiceId } });
                    };
                    return (
                      <article
                        key={`${ticket.ticket_source_id}:${ticket.ticket_source_version}`}
                        className={`p-5 transition ${invoiceId ? "cursor-pointer hover:bg-blue-50/60 focus-within:bg-blue-50/60 dark:hover:bg-blue-950/20" : "hover:bg-slate-50 dark:hover:bg-gray-950"}`}
                        onClick={invoiceId ? openInvoice : undefined}
                        onKeyDown={invoiceId ? (event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            openInvoice();
                          }
                        } : undefined}
                        role={invoiceId ? "link" : undefined}
                        tabIndex={invoiceId ? 0 : undefined}
                        aria-label={invoiceId ? `Open invoice ${ticket.lineage.invoice_no}` : undefined}
                      >
                        <div className="flex flex-wrap items-start gap-4">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="font-mono text-sm font-semibold text-slate-950 dark:text-white">{ticket.chain.ticket || ticket.ticket_source_id}</h3>
                              <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${statusTone(ticket.status)}`}>
                                {ticket.status.replaceAll("_", " ")}
                              </span>
                              <span className="text-xs text-slate-500">v{ticket.ticket_source_version}</span>
                            </div>
                            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                              Customer {String(ticket.customer_job_profile.customer_ref || "unresolved")} · site {String(ticket.customer_job_profile.job_site_ref || "unresolved")} · mix {String(ticket.customer_job_profile.mix_ref || "unresolved")}
                            </p>
                            <p className="mt-1 text-xs text-slate-500">
                              Order {ticket.chain.order || "missing"} → load {ticket.chain.load || "missing"} → batch {ticket.chain.batch || "missing"}
                            </p>
                          </div>
                          <div className="text-right">
                            <div className="text-xs uppercase tracking-wide text-slate-500">Billable total</div>
                            <div className="mt-1 text-lg font-semibold text-slate-950 dark:text-white">
                              {ticket.pricing ? money(ticket.pricing.total, ticket.pricing.currency) : "—"}
                            </div>
                          </div>
                        </div>

                        {ticket.pricing && (
                          <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200 dark:border-gray-800">
                            <table className="w-full text-left text-sm">
                              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-gray-950">
                                <tr><th className="px-3 py-2">Pricing rule</th><th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2 text-right">Rate</th><th className="px-3 py-2 text-right">Amount</th></tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100 dark:divide-gray-800">
                                {ticket.pricing.lines.map((line) => (
                                  <tr key={`${line.line_no}:${line.component_key}`}>
                                    <td className="px-3 py-2"><span className="font-medium text-slate-800 dark:text-slate-200">{line.description}</span><span className="ml-2 font-mono text-xs text-slate-400">{line.component_key}</span></td>
                                    <td className="px-3 py-2 text-right font-mono">{quantity(line.quantity)}</td>
                                    <td className="px-3 py-2 text-right font-mono">{money(line.unit_price, ticket.pricing?.currency)}</td>
                                    <td className="px-3 py-2 text-right font-mono font-semibold">{money(line.amount, ticket.pricing?.currency)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {ticket.exceptions.length > 0 && (
                          <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">
                            <div className="font-semibold">Resolve before invoicing</div>
                            <ul className="mt-2 space-y-2">
                              {ticket.exceptions.map((item) => (
                                <li key={item.code}>
                                  <span className="font-mono text-xs font-semibold">{item.code}</span>: {item.detail}
                                  <span className="ml-1 text-rose-700">Next: {item.next_action.replaceAll("_", " ")}.</span>
                                </li>
                              ))}
                            </ul>
                            <Link to="/settings/integrations" className="mt-3 inline-flex rounded-md border border-rose-300 bg-white px-3 py-1.5 font-semibold text-rose-800 hover:bg-rose-100">
                              Review source mappings
                            </Link>
                          </div>
                        )}

                        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                          <div className="text-xs text-slate-500">
                            {ticket.lineage.invoice_id
                              ? `Invoice ${ticket.lineage.invoice_no} · ${ticket.lineage.invoice_status} · paid ${money(ticket.lineage.paid_amount)}`
                              : ticket.delivered_not_invoiced
                                ? "Accepted delivery is not yet invoiced."
                                : "Not billable until the named exceptions are resolved."}
                          </div>
                          <div className="flex flex-wrap gap-2" onClick={(event) => event.stopPropagation()}>
                            {ticket.lineage.journal_id && (
                              <Link to="/journals/$journalId" params={{ journalId: ticket.lineage.journal_id }} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:border-blue-400 hover:text-blue-700">
                                Open journal
                              </Link>
                            )}
                            {(ticket.lineage.receipt_ids ?? []).map((paymentId, index) => (
                              <Link key={paymentId} to="/payments/$paymentId" params={{ paymentId }} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:border-blue-400 hover:text-blue-700">
                                Open receipt {index + 1}
                              </Link>
                            ))}
                            {ticket.lineage.invoice_id ? (
                              <Link to="/invoices/ar/$invoiceId" params={{ invoiceId: ticket.lineage.invoice_id }} className="rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700">
                                Open invoice
                              </Link>
                            ) : ticket.invoice_proposal ? (
                              <button
                                type="button"
                                onClick={() => createInvoice.mutate(ticket.ticket_source_id)}
                                disabled={createInvoice.isPending}
                                className="rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                              >
                                {createInvoice.isPending ? "Creating draft…" : "Create draft invoice"}
                              </button>
                            ) : null}
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </section>

            <p className="text-center text-xs text-slate-400">
              Projection {data.projection.projection_sha256.slice(0, 12)} · composition {data.projection.composition_sha256.slice(0, 12)}
            </p>
          </>
        )}
      </main>
    </div>
  );
}
